import { useCallback, useContext, useMemo } from "react";
import CheckUpContext from "../../context/check-up";
import Requests from "../requests";
import dayjs from "dayjs";
import localeData from "dayjs/plugin/localeData";
import LocalizedFormat from "dayjs/plugin/localizedFormat";
import useCheckUpDeleteMutation from "../../hooks/mutations/useCheckupDeleteMutation";
import "dayjs/locale/it";
import "dayjs/locale/fr";
import "dayjs/locale/es";
import "dayjs/locale/pt";
import useCheckUpCompleteMutation from "../../hooks/mutations/useCheckupCompleteMutation";
dayjs.extend(LocalizedFormat);
dayjs.extend(localeData);

const CheckUpRequests = () => {
  const {
    labels,
    appLang,
    status,
    currentRequest,
    requests: currentRequests,
    setCurrentRequest,
    setNavigation,
    isRequestFetching: isFetching,
  } = useContext(CheckUpContext);

  const sortingOptions = useMemo(
    () => [
      {
        value: "PACKAGE_AZ",
        label: labels?.WELFARE_CHECKUP_PACKAGE_AZ || "Pacchetto AZ",
        desc: true,
      },
      {
        value: "PACKAGE_ZA",
        label: labels?.WELFARE_CHECKUP_PACKAGE_ZA || "Pacchetto ZA",
        desc: false,
      },
      { value: "CENTER_AZ", label: labels?.WELFARE_CHECKUP_CENTER_AZ || "Clinica AZ", desc: true },
      { value: "CENTER_ZA", label: labels?.WELFARE_CHECKUP_CENTER_ZA || "Clinica ZA", desc: false },
      { value: "DATE_AZ", label: labels?.WELFARE_BORSE_NEWEST || "Newest", desc: true },
      { value: "DATE_ZA", label: labels?.WELFARE_BORSE_OLDEST || "Oldest", desc: false },
      { value: "STATUS_AZ", label: labels?.WELFARE_BORSE_STATUS_AZ || "Status AZ", desc: true },
      { value: "STATUS_ZA", label: labels?.WELFARE_BORSE_STATUS_ZA || "Status ZA", desc: false },
    ],
    [labels]
  );

  const columnItemTitle = useMemo(
    () => [
      {
        label: labels?.WELFARE_CHECKUP_PACKAGE || "Pacchetto",
        className: "table__box--big",
        name: "PACKAGE",
        getContent: (request) => `${request?.packageSelected?.pack?.name || "-"}`,
      },
      {
        label: labels?.WELFARE_CHECKUP_CENTER || "Clinica",
        className: "table__box--big",
        name: "CENTER",
        getContent: (request) => `${request?.clinic?.name || "-"}`,
      },
      {
        label: labels?.WELFARE_CHECKUP_DATE || "Date",
        name: "DATE",
        getContent: (request) =>
          request?.completionDate
            ? dayjs(Number(request?.completionDate))
                .locale(appLang?.value || "it")
                .format("L")
            : "-",
      },
      {
        label: labels?.WELFARE_BORSE_STATUS || "Status",
        name: "STATUS",
        getContent: (request) => (
          <span className={`table__status table__status--${request?.status.toLowerCase()}`}>
            {labels?.[`WELFARE_STATUS_${request?.status.toUpperCase()}`] || request.status}
          </span>
        ),
      },
    ],
    [labels, appLang]
  );

  const { mutateAsync: deleteRequest, isError, reset, isLoading } = useCheckUpDeleteMutation();

  const handleDeleteRequest = async (request) => {
    await deleteRequest({ request });
  };

  const handleGoToDetail = useCallback(
    (request) => {
      setCurrentRequest(request);
      setNavigation("detail");
    },
    [setCurrentRequest, setNavigation]
  );

  const { mutateAsync: submitRequest, isError: isSubmitError } = useCheckUpCompleteMutation();

  const handleSubmitRequest = async (request) => {
    await submitRequest(request);
  };

  return (
    <div className="app-content__section check-up-content__requests">
      {isFetching ? (
        <div className="loading-spinner" />
      ) : currentRequests?.length > 0 ? (
        <>
          <div className="app-content__title-wrapper">
            <h2 className="app-content__title">
              {labels?.WELFARE_CHECKUP_BOOKING_REQUESTS_LIST || "Storico check-up"}
            </h2>
          </div>
          <div
            className="app-content__text"
            dangerouslySetInnerHTML={{
              __html:
                labels?.WELFARE_CHECKUP_REQUESTS_DESCRIPTION ||
                "<p>I check-up effettuati rimarranno in questo elenco fino a 2 anni dopo la data di erogazione.<br/>Superati i due anni verrano eliminati dall’elenco come previsto dalle normative sulla privacy.</p>",
            }}
          />
          <Requests
            currentRequests={currentRequests}
            setCurrentRequest={handleGoToDetail}
            labels={labels}
            sortingOptions={sortingOptions}
            columnItemTitle={columnItemTitle}
            hideSendCondition={(request) => request?.lastCompletedStep === 2}
            deleteObj={{
              deleteRequest: handleDeleteRequest,
              isError: isError,
              reset: reset,
              isLoading: isLoading,
            }}
            submitObj={{
              submitRequest: handleSubmitRequest,
              isError: isSubmitError,
            }}
            showChevron={(request) => ["COMPLETED", "CANCELED"].includes(request?.status)}
          />
        </>
      ) : (
        status === "ready-to-book" &&
        !currentRequest && (
          <div
            className="error"
            dangerouslySetInnerHTML={{ __html: labels?.WELFARE_CHECKUP_NO_REQUESTS_YET }}
          />
        )
      )}
    </div>
  );
};

export default CheckUpRequests;
